import { useMemo } from 'react';

import * as yup from 'yup';
import { useRegionsQuery } from '@cast/shared-inventory-data-access';

import { Cloud } from '@cast/shared-types';
import { makeValidateSync } from '@cast/utils';
import { CloseableAlert } from 'components/alerts';

import { CloseDrawerGuard } from 'components/drawer';
import {
  ControlWithDescription,
  InlineWithDescription,
} from 'components/form-layouts';
import { useAbility } from '@cast/shared-ability-data-access';
import { CloudSettingsSection } from './CloudSettingsSection';

import { EdgeLocationDrawerFooter } from './EdgeLocationDrawerFooter';
import { LocationSettingsSection } from './LocationSettingsSection';
import { SelectAddonsField } from './SelectAddonsField';
import { apiAddonsToFormModelAddons } from '../_utils';
import { formModelAddonsSchema } from '../_validation';
import { CUSTOM_EDGE_PROVIDER, SUPPORTED_EDGE_PROVIDERS } from '../constants';
import { useGetOmniClusterFromList } from '../hooks';
import {
  EdgeLocationFormValues,
  EnhancedEdgeLocation,
  EdgeLocationInitialData,
} from '../types';
import { clusterToProvider } from '../utils';
import { useCluster } from '@cast/shared-cluster-context-data-access';

import { FormMode, RffForm, RffInput, RffTextArea } from '@cast/shared-rff-ui';

import { Typography, Stack, DrawerContent, tokens } from '@cast/design-system';

const validate = makeValidateSync(
  yup.object({
    provider: yup
      .string()
      .test('provider-required', 'Provider is required', (value) => {
        if (value === CUSTOM_EDGE_PROVIDER) return true;
        return (
          value === Cloud.aws || value === Cloud.gcp || value === Cloud.oracle
        );
      }),
    region: yup.string().when('provider', {
      is: (value: string) => value !== CUSTOM_EDGE_PROVIDER,
      then: (schema) => schema.required('Region is required'),
      otherwise: (schema) => schema.optional(),
    }),
    locationName: yup
      .string()
      .max(30, 'Location name must be less than 30 characters')
      .matches(
        /^[a-z]([-a-z0-9]*[a-z0-9])?$|^$/,
        'Invalid format for location name'
      )
      .required('Location name is required'),
    description: yup.string(),
    projectId: yup.string().when('provider', {
      is: Cloud.gcp,
      then: (schema) => schema.required('Project ID is required'),
      otherwise: (schema) => schema.optional(),
    }),
    addons: formModelAddonsSchema,
  })
);

type EdgeLocationDetailsFormProps = {
  mode: FormMode;
  edgeLocation?: EnhancedEdgeLocation;
  isLoading?: boolean;
  onClose: () => void;
  onSubmit: (formValues: EdgeLocationFormValues) => void;
  initialData?: EdgeLocationInitialData;
  guardOnClose?: boolean;
};

export const EdgeLocationDetailsForm = ({
  mode,
  edgeLocation,
  isLoading = false,
  onClose,
  onSubmit,
  initialData,
  guardOnClose = true,
}: EdgeLocationDetailsFormProps) => {
  const isEditMode = mode === 'edit' && !!edgeLocation;
  const onlyClusterProviderEdges = useAbility().can(
    'view',
    'FeatureOnlyClusterProviderEdges'
  );
  const { cluster } = useCluster();
  const { regions } = useRegionsQuery();
  const { clusterDetails: omniClusterDetails } = useGetOmniClusterFromList({
    clusterId: cluster.id,
  });

  const initialValues = useMemo(() => {
    if (!isEditMode) {
      if (initialData) {
        const initSupportedProvider =
          SUPPORTED_EDGE_PROVIDERS.find(
            (provider) => provider === initialData.provider
          ) ?? '';

        const matchedRegion = regions?.find(
          (r) =>
            r.csp === initSupportedProvider && r.name === initialData.region
        );

        const initSupportedProviderRegion = matchedRegion?.name ?? '';
        const initLocationName = matchedRegion?.displayName ?? '';

        const allowPrefillData =
          !onlyClusterProviderEdges ||
          clusterToProvider(cluster) === initSupportedProvider;

        const initProjectId =
          initSupportedProvider === Cloud.gcp && initialData.providerProjectId
            ? initialData.providerProjectId
            : '';

        return {
          provider: onlyClusterProviderEdges
            ? clusterToProvider(cluster)
            : initSupportedProvider,
          projectId: allowPrefillData ? initProjectId : '',
          region: allowPrefillData ? initSupportedProviderRegion : '',
          locationName: allowPrefillData ? initLocationName : '',
          description: '',
          addons: {},
        };
      }

      return {
        provider: onlyClusterProviderEdges
          ? clusterToProvider(cluster)
          : Cloud.aws,
        projectId: '',
        region: '',
        locationName: '',
        description: '',
        addons: {},
      };
    }

    return {
      provider: edgeLocation.provider,
      projectId: edgeLocation?.gcp?.projectId,
      region: edgeLocation?.region,
      locationName: edgeLocation?.name,
      description: edgeLocation?.description,
      addons: apiAddonsToFormModelAddons(
        edgeLocation.edgeClusterSpec?.addons || []
      ),
    };
  }, [
    isEditMode,
    edgeLocation,
    onlyClusterProviderEdges,
    cluster,
    initialData,
    regions,
  ]);

  return (
    <RffForm
      initialValues={initialValues}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: 0,
        height: '100%',
      }}
      onSubmit={onSubmit}
      validate={validate}
      mode={mode}
      testId="edge-location-details-form"
    >
      {({ valid, dirty, handleSubmit }) => {
        const canSubmit = isEditMode ? dirty : valid;

        return (
          <>
            <DrawerContent sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <Typography
                variant="L12R"
                color={tokens.theme.fg.structural.secondary}
                mb={8}
              >
                {isEditMode ? ' Edit edge location' : 'Create edge location'}
              </Typography>

              <Typography variant="h5" mb={isEditMode ? 0 : 12}>
                {isEditMode
                  ? 'Edit location configuration'
                  : 'Configure new edge location'}
              </Typography>
              {isEditMode && (
                <CloseableAlert
                  color="info"
                  size="small"
                  sx={{ mt: 18, mb: 24 }}
                  testId="edge-location-edit-alert"
                  closeable="edge-location-edit-alert"
                >
                  Cloud provider and region settings cannot be changed after
                  setup. To use a different region or provider, create a new
                  edge location.
                </CloseableAlert>
              )}
              <InlineWithDescription
                gridTemplateColumns="266px auto"
                gap={42}
                py={16}
                sx={{
                  backgroundColor: tokens.theme.bg.status.infoSubtle,
                  mx: -32,
                  px: 32,
                }}
                description={
                  <ControlWithDescription label="Location settings">
                    Choose a provider where you want to provision additional
                    compute capacity.
                  </ControlWithDescription>
                }
                controls={
                  isEditMode ? (
                    <CloudSettingsSection showCustomProviderOption />
                  ) : (
                    <LocationSettingsSection />
                  )
                }
              />
              <InlineWithDescription
                gridTemplateColumns="266px auto"
                gap={42}
                py={12}
                sx={
                  isEditMode
                    ? {}
                    : {
                        borderBottom: 'none',
                      }
                }
                description={
                  <ControlWithDescription label="Location details">
                    Define how this edge location appears in your cluster.
                  </ControlWithDescription>
                }
                controls={
                  <Stack gap={16}>
                    <RffInput
                      testId="edge-location-name-input"
                      name="locationName"
                      label="Location name"
                      inputProps={{
                        placeholder: 'e.g. europe-west4',
                      }}
                      readOnly={isEditMode}
                    />
                    <RffTextArea
                      testId="edge-location-description"
                      name="description"
                      label="Description (Optional)"
                      rows={2}
                      inputProps={{
                        placeholder: 'e.g. Backup region for GPU workloads',
                      }}
                    />
                  </Stack>
                }
              />
              {isEditMode && (
                <InlineWithDescription
                  gridTemplateColumns="266px auto"
                  gap={42}
                  py={12}
                  sx={{
                    borderBottom: 'none',
                  }}
                  description={
                    <ControlWithDescription label="Select add-ons">
                      Cast AI components installed on nodes in this edge
                      location. OMNI Agent is required; the rest are optional
                      based on your workload needs.
                    </ControlWithDescription>
                  }
                  controls={
                    <SelectAddonsField
                      provider={edgeLocation.provider}
                      name="addons"
                      testIdPrefix="edit-addon"
                      omniAgentVersion={omniClusterDetails?.omniAgentVersion}
                    />
                  }
                />
              )}
            </DrawerContent>

            <EdgeLocationDrawerFooter
              isDisabled={!canSubmit}
              submitButtonTestId={
                isEditMode
                  ? 'save-edge-location-button'
                  : 'create-edge-location-button'
              }
              submitButtonLabel={isEditMode ? 'Save changes' : 'Next'}
              showSubmitIcon={!isEditMode}
              isLoading={isLoading}
              onClose={onClose}
              onSubmit={handleSubmit}
            />

            <CloseDrawerGuard onLeave={onClose} formGuard={guardOnClose} />
          </>
        );
      }}
    </RffForm>
  );
};
